import React, { useState, useEffect } from 'react';
import {
  X,
  Gift,
  Percent,
  Star,
  Sparkles,
  Fire,
  Crown,
  Heart,
  Zap,
  Tag,
  ArrowRight,
  CheckCircle,
  Calendar,
  Clock
} from 'lucide-react';

const OffersModal = ({ 
  isOpen, 
  onClose, 
  darkMode = false,
  offerData = null // Datos de la oferta que vienen del admin
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  // Datos por defecto si no vienen del admin
  const defaultOffer = {
    id: 1,
    title: "🎉 ¡Oferta Especial!",
    subtitle: "Descuento exclusivo por tiempo limitado",
    description: "Aprovecha esta increíble oportunidad y obtén acceso a todos nuestros productos con un descuento especial.",
    discount_type: "percentage", // percentage, fixed, special
    discount_value: 50,
    code: null, // ← CAMBIADO: Sin código por defecto
    expiry_date: "2024-12-31",
    button_text: null, // ← CAMBIADO: Sin botón por defecto
    button_url: null, // ← CAMBIADO: Sin URL por defecto
    image_url: null,
    background_type: "gradient", // gradient, solid, image
    background_color: "purple",
    is_active: true,
    show_timer: true,
    auto_show: true,
    show_frequency: "once" // once, daily, always
  };

  const currentOffer = offerData || defaultOffer;

  useEffect(() => {
    if (isOpen && currentOffer.is_active) {
      // Verificar si debemos mostrar el modal según la frecuencia
      const shouldShowModal = checkIfShouldShow();
      
      if (shouldShowModal) {
        setShouldShow(true);
        setTimeout(() => setIsVisible(true), 100);
      }
    }
  }, [isOpen]);

  const checkIfShouldShow = () => {
    const storageKey = `nexus_offer_${currentOffer.id}`;
    const lastShown = localStorage.getItem(storageKey);
    const today = new Date().toDateString();

    switch (currentOffer.show_frequency) {
      case 'once':
        return !lastShown;
      case 'daily':
        return lastShown !== today;
      case 'always':
        return true;
      default:
        return !lastShown;
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      setShouldShow(false);
      
      // Guardar en localStorage según la frecuencia
      const storageKey = `nexus_offer_${currentOffer.id}`;
      if (currentOffer.show_frequency === 'once') {
        localStorage.setItem(storageKey, 'shown');
      } else if (currentOffer.show_frequency === 'daily') {
        localStorage.setItem(storageKey, new Date().toDateString());
      }
      
      onClose();
    }, 300);
  };

  const handleClaim = () => {
    // Copiar código de descuento si existe
    if (currentOffer.code) {
      navigator.clipboard.writeText(currentOffer.code);
    }
    
    // Redirigir si hay URL
    if (currentOffer.button_url && currentOffer.button_url !== '#') {
      window.open(currentOffer.button_url, '_blank');
    }
    
    handleClose();
  };

  const getDiscountText = () => {
    if (currentOffer.discount_type === 'percentage') {
      return `${currentOffer.discount_value}% OFF`;
    } else if (currentOffer.discount_type === 'fixed') {
      return `$${currentOffer.discount_value} OFF`;
    } else {
      return 'OFERTA ESPECIAL';
    }
  };

  const getBackgroundClasses = () => {
    if (currentOffer.background_type === 'gradient') {
      switch (currentOffer.background_color) {
        case 'purple':
          return 'bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800';
        case 'blue':
          return 'bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800';
        case 'green':
          return 'bg-gradient-to-br from-green-600 via-green-700 to-emerald-800';
        case 'red':
          return 'bg-gradient-to-br from-red-600 via-red-700 to-pink-800';
        case 'orange':
          return 'bg-gradient-to-br from-orange-600 via-orange-700 to-red-800';
        default:
          return 'bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800';
      }
    } else if (currentOffer.background_type === 'solid') {
      return `bg-${currentOffer.background_color}-600`;
    }
    return 'bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!shouldShow || !currentOffer.is_active) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
      isVisible ? 'opacity-100' : 'opacity-0'
    }`}>
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className={`relative transform transition-all duration-300 ${
        isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
      }`}>
        <div className={`relative max-w-md w-full mx-auto rounded-2xl overflow-hidden shadow-2xl ${getBackgroundClasses()}`}>
          
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-white bg-opacity-10 rounded-full"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white bg-opacity-5 rounded-full"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <Sparkles className="w-32 h-32 text-white opacity-5" />
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors text-white"
          >
            <X size={20} />
          </button>

          {/* Content */}
          <div className="relative p-8 text-center text-white">
            {/* Icon/Badge */}
            <div className="mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white bg-opacity-20 rounded-full mb-4">
                {currentOffer.discount_type === 'percentage' ? (
                  <Percent className="w-8 h-8" />
                ) : currentOffer.discount_type === 'fixed' ? (
                  <Tag className="w-8 h-8" />
                ) : (
                  <Gift className="w-8 h-8" />
                )}
              </div>
              
              {/* Discount Badge */}
              <div className="inline-block bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full font-bold text-lg mb-4">
                {getDiscountText()}
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold mb-2">{currentOffer.title}</h2>
            
            {/* Subtitle */}
            {currentOffer.subtitle && (
              <p className="text-purple-100 mb-4 text-lg">{currentOffer.subtitle}</p>
            )}

            {/* Description */}
            <p className="text-white text-opacity-90 mb-6 leading-relaxed">
              {currentOffer.description}
            </p>

            {/* ✅ CÓDIGO DE DESCUENTO - SOLO SI EXISTE */}
            {currentOffer.code && (
              <div className="bg-white bg-opacity-20 rounded-lg p-4 mb-6">
                <p className="text-sm text-purple-100 mb-1">Código de descuento:</p>
                <div className="flex items-center justify-center space-x-2">
                  <code className="bg-white text-purple-800 px-3 py-1 rounded font-mono font-bold">
                    {currentOffer.code}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(currentOffer.code)}
                    className="text-white hover:text-purple-200 transition-colors"
                    title="Copiar código"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* ✅ TIMER - SOLO SI SHOW_TIMER Y EXPIRY_DATE EXISTEN */}
            {currentOffer.show_timer && currentOffer.expiry_date && (
              <div className="flex items-center justify-center space-x-2 mb-6 text-yellow-200">
                <Clock size={16} />
                <span className="text-sm">Válido hasta: {formatDate(currentOffer.expiry_date)}</span>
              </div>
            )}

            {/* ✅ BOTÓN DE ACCIÓN - SOLO SI BUTTON_TEXT EXISTE */}
            {currentOffer.button_text && (
              <button
                onClick={handleClaim}
                className="w-full bg-white text-purple-700 py-4 px-6 rounded-lg font-bold text-lg hover:bg-purple-50 transition-colors transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg"
              >
                <span>{currentOffer.button_text}</span>
                <ArrowRight size={20} />
              </button>
            )}

            {/* ✅ TEXTO LEGAL - SOLO SI HAY CÓDIGO O BOTÓN */}
            {(currentOffer.code || currentOffer.button_text) && (
              <p className="text-xs text-purple-200 mt-4 opacity-75">
                *Términos y condiciones aplican. Oferta válida por tiempo limitado.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook para usar el modal de ofertas
export const useOffersModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [offerData, setOfferData] = useState(null);

  const showOffer = (data = null) => {
    setOfferData(data);
    setIsOpen(true);
  };

  const hideOffer = () => {
    setIsOpen(false);
    setOfferData(null);
  };

  return { isOpen, offerData, showOffer, hideOffer };
};

export default OffersModal;